/**
 * OpenEmailLink — the "Open Email ↗" hop back to the email a brief was
 * prepared from. Shared by the Ready-to-start card (`ReadyBriefsQueue`) and
 * the launch form (`LaunchForm`), so the two render one control rather than
 * two lookalikes that drift.
 *
 * Also the step drawers' header action (the pane's `SourceRequestRow` and
 * ora's monitor drawer), so every surface names the hop the same way.
 *
 * Presentation only: the host decides whether an email can be opened at all
 * (it needs a Graph id and a host that can open one) and owns the open itself.
 * A host with Office.js passes `onClick`; a browser page, which cannot open a
 * message in Outlook, passes an Outlook-on-the-web `href` instead.
 */

import React from "react";
import { TYPE_BODY } from "../paneTypography";

export const OPEN_EMAIL_LABEL = "Open Email";

/** What a host's open reports back; `message` is written for the AM. */
export type OpenEmailResult = { ok: true } | { ok: false; message: string };

export type OpenEmailLinkProps = {
  testId: string;
  className?: string;
} & (
  | { onClick: () => void; href?: never }
  | { href: string; onClick?: never }
);

export function OpenEmailLink({
  onClick,
  href,
  testId,
  className = "",
}: OpenEmailLinkProps): React.ReactElement {
  const classes = `inline-flex flex-shrink-0 items-center gap-1 border-b-2 border-sunrise pb-px ${TYPE_BODY} text-primary-gray-light-700 ${className}`.trim();
  const content = (
    <>
      {OPEN_EMAIL_LABEL}
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M7 17 17 7M9 7h8v8" />
      </svg>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={classes}
      >
        {content}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={classes}>
      {content}
    </button>
  );
}

export default OpenEmailLink;
